function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

// custom global function
class FreqSelector extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.changeEvent = new Event('change', { bubbles: true })

    this.querySelector('.rc_select__frequency').addEventListener('change', this.ChangeFrequencySelector.bind(this));
  }

  ChangeFrequencySelector(event) {
    event.preventDefault();
    this.dispatchEvent(this.changeEvent);
  }
}

customElements.define('frequency-selector', FreqSelector);
// custom global function

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};



class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);


class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );

    // As of June 6 2025 we will move this to CountrySelector  class
    //for country and language dropdonw in header
    // if (this.querySelector('.pmd-custom-dropdown__box')) {
    //   this.querySelector('.language_selector .pmd-custom-dropdown__box').addEventListener('click', this.toggleLangDropdown.bind(this));

    //   if(this.querySelector('.country_selector .pmd-custom-dropdown__box')) {
    //     this.querySelector('.country_selector .pmd-custom-dropdown__box').addEventListener('click', this.toggleCountryDropdown.bind(this));
    //   }
      

    //   const countryDropdownSelectedNode = this.querySelector(".country_selector .pmd-custom-dropdown__header");
    //   if(countryDropdownSelectedNode) {
    //     countryDropdownSelectedNode.addEventListener("keydown", e =>
    //       this.toggleListVisibility(e,'country')
    //     );
    //   }

    //   const langDropdownSelectedNode = this.querySelector(".language_selector .pmd-custom-dropdown__header");
    //   langDropdownSelectedNode.addEventListener("keydown", e =>
    //     this.toggleListVisibility(e,'lang')
    //   );

    //   this.customDropdownsLanguages = this.querySelectorAll('.language_selector .pmd-custom-dropdown__body__item');
    //   this.customDropdownsLanguages.forEach(link => {
    //     link.addEventListener("keydown", e => {
    //       switch (e.keyCode) {
    //         case ENTER_KEY_CODE:
    //           this.selectLanguage(e)
    //           return;
    //         default:
    //            return;
    //       }
    //     });
    //     link.addEventListener('click', this.selectLanguage.bind(this))
    //   });
    //   // this.querySelector('.country-lang-btn').addEventListener('click', this.changeRegionOrLanguage.bind(this));
    //   var th = this;
    //   this.querySelector('.country-lang-btn').addEventListener("click", function(e) {
    //     setCookie("stayregion", '1', 1);
    //     th.changeRegionOrLanguage(e);
    //   });
    // }

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);

  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);


class CountrySelector extends HTMLElement {
  constructor() {
    super();

      this.redirectDomains = [{
        'us': 'https://pmdbeauty.com',
        'au': 'https://au.pmdbeauty.com',
        'gb': 'https://uk.pmdbeauty.com',
        'european_union': 'https://eu.pmdbeauty.com',
        'ca': 'https://ca.pmdbeauty.com',
        'it': 'https://pmdbeauty.it',
        'mx': 'https://pmdbeauty.com.mx',
        'kr': 'https://www.pmdbeauty.co.kr'
      }];

      this.currentSite = window.store;
      this.selectedCountry = '';
      this.currentQueryString = window.location.search;
      this.localSelectedLanguage, this.localSelectedCountry
      this.urlParams = new URLSearchParams(this.currentQueryString);
      this.urlCountry = this.urlParams.has('country');
      this.urlLang = this.urlParams.has('language');
  }

  connectedCallback() {
    this.detailsElement = this.querySelector('.grg-dropdown-country-switch-wrapper')
    this.currentCountry = this.querySelector('.grg-selected-country-indicator')
    this.countryOptions = this.querySelectorAll('.quick_country_selector_grg > li')

    // initialize the current country bubble
    this.displayCurrentCountry(this.currentSite);

    this.detailsElement.addEventListener('click', () => {
      this.detailsElement.open = true

      // create an event listener only when detailsElement is open
      document.addEventListener('click', this.detailsOpener.bind(this))
    })

    // add event listeners to all country bubbles
    this.countryOptions.forEach(option => {
      option.addEventListener('click', () => {
        this.selectedCountry = option.dataset.alpha
        this.displayCurrentCountry(this.selectedCountry);
        this.changeRegion();
      })
    })

  }

  detailsOpener(e) {
      if(!this.detailsElement.contains(e.target)) {
        this.detailsElement.open = false
        // make sure to remove the event listener in the document
        document.removeEventListener('click', this.detailsOpener);
      }
  }

  displayCurrentCountry(countryCode) {
    // we must first assume that we dont know the country-flag-something
    var elementClass = this.currentCountry.classList
    elementClass.forEach(clss => {
      if(clss.startsWith('country-flag-')) {
        elementClass.remove(clss);
      }
    })

    // this is the time we update the country-flag-${countryAlphaCode}
    this.currentCountry.classList.add(`country-flag-${countryCode}`)
    this.currentCountry.dataset.alpha = countryCode;
  }

  changeRegion() {
    if (this.selectedCountry) {
      // means if u r on au store and selected us store
      if (this.selectedCountry != this.currentSite) {

        // below will return https://au.pmdbeauty.com if for example selected site is au
        var redirectUrl = this.redirectDomains[0][this.selectedCountry];
        var fullURL = new URL(window.location.href);
        // we need a base url to cover everything not listed in the redirectDomains
        var baseURL = fullURL.origin; 

        if(redirectUrl) {
            window.location.href = redirectUrl + fullURL.pathname +'?country='+this.selectedCountry+'&stayRegion=1';
        } else {
            window.location.href = baseURL + fullURL.pathname +'?country='+this.selectedCountry;
        }
      }




    }
  }



}

customElements.define('country-selector', CountrySelector);

class BulkOrderForm extends HTMLElement {
  constructor() {
    super();

    // we will have to create a container to store the event listeners so that we can remove them when the element is disconnected from the DOM
    // this ensures that the stored listener is the same as the one we added to the element and we can successfully remove it later
    //By “listener” we mean the function you give to addEventListener that will be called when the event fires.
    this._listeners = {};

  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.totalElement = this.querySelector('.bulk-order__pricing .sc-qs-bof-grand-total-price');
    this.subtotalElements = this.querySelectorAll('.variant-item__inner .subtotal');
    //this.submitButton.addEventListener('click', this.onSubmit.bind(this));


    // Select all inputs with the class "quantity-input"
    // by storing the quantity inputs as a class property, we can make sure that we are removing the same event listener in the disconnectedCallback
    this.quantityInputs = this.querySelectorAll('.form-control.quantity-input');

    // create the reset event listener and store it in the _listeners object so that we can remove it later in the disconnectedCallback
    this._listeners.updateVariantSubtotalListener = event => {
      const quantity = event.target.value; // current number typed
      const variantId = event.target.dataset.variantId; // from data-variant-id
      const price = parseFloat(event.target.dataset.price); // from data-price

      //console.log(`Variant ${variantId} updated to ${value}, price: ${price}`);
      // You can add your logic here, e.g., update totals or send AJAX request
      this.updateVariantSubtotal(variantId, quantity, price);
    }

    // Attach an event listener to each input
    this.quantityInputs.forEach(input => {
      input.addEventListener('input', this._listeners.updateVariantSubtotalListener);
    });

    // create the reset event listener and store it in the _listeners object so that we can remove it later in the disconnectedCallback
    this._listeners.resetVariantSubtotalListener = this.resetVariantSubtotal.bind(this)
    this.form.addEventListener('reset', this._listeners.resetVariantSubtotalListener);

    // create the reset event listener and store it in the _listeners object so that we can remove it later in the disconnectedCallback
    this._listeners.submitBulkOrderFormListener = this.submitBulkOrderForm.bind(this)
    this.form.addEventListener('submit', this._listeners.submitBulkOrderFormListener);

   }

   disconnectedCallback() {
    //remove the submit event listener from the form
    this.form.removeEventListener('reset', this._listeners.resetVariantSubtotalListener);

    //remove the submit event listener from the form
    this.form.removeEventListener('reset', this._listeners.submitBulkOrderFormListener);

    // Remove the event listener from each input
    this.quantityInputs.forEach(input => {
      input.removeEventListener('input', this._listeners.updateVariantSubtotalListener);
    });
    
   }

   resetVariantSubtotal() {
    this.subtotalElements.forEach(element => {
      element.textContent = `$0.00`;
      element.dataset.variantSubtotal = 0;
    });
    this.totalElement.textContent = `$0.00`;
   }

   updateVariantSubtotal(variantId, quantity, price) {
    let subtotal = 0;
    if (quantity && price) {
      subtotal = quantity * price;
    } 

    const subtotalElement = this.querySelector(`.subtotal-variant-${variantId}`);
    if (subtotalElement) {
      // Update the visible text
      subtotalElement.textContent = `$${subtotal.toFixed(2)}`;

      // Update the data attribute 
      subtotalElement.dataset.variantSubtotal = subtotal;
    }

    this.updateTotal()
   }

   updateTotal() {
    let total = 0;
    this.subtotalElements.forEach(element => {
      const variantSubtotal = parseFloat(element.dataset.variantSubtotal);
      if (!isNaN(variantSubtotal)) {
        total += variantSubtotal;
      }
    });

    if (this.totalElement) {
      this.totalElement.textContent = `$${total.toFixed(2)}`;
    }
   }

  submitBulkOrderForm(event) {
    event.preventDefault();
    this.disableButton();
    //console.log('Bulk Add to Cart initiated', event);

    // Create a FormData object from the form
    // const formData = new FormData(this.form);
    // console.log('FormData entries:', formData.entries() );

    // Gather the variant IDs and quantities
    const items = [];

    // Loop through all quantity inputs
    this.quantityInputs.forEach(input => {
      const quantity = parseInt(input.value, 10);
      const variantId = input.dataset.variantId;
      //const price = parseFloat(input.dataset.price);

      if (quantity > 0) {
        items.push({
          'id': variantId,
          'quantity': quantity
          //price
        });
      }
    });

    this.bulkAddToCart(items);
  }

  disableButton() {
    if (this.submitButton) {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.classList.add('loading');
      this.submitButton.querySelector('.loading-overlay__spinner').classList.remove('hidden');
    }
  }

  enableButton() {
    if (this.submitButton) {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.classList.remove('loading');
      this.submitButton.querySelector('.loading-overlay__spinner').classList.add('hidden');
    }
  }

  bulkAddToCart(items) {
      //console.log('Adding items to cart:', items);
      if (items.length === 0) {
        alert('Please enter at least one quantity');
        this.enableButton();
        return;
      }

      // Send items to Shopify Cart API
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: items }),
        headers: { 
          'Content-Type': 'application/json'
        }
      })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }
      return response.json();
    })
    .then(data => {
      //console.log('Successfully added to cart:', data);
      this.resetButton.click(); // Reset the form after successful submission
      this.enableButton()
      window.location.href = '/cart';
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      alert('There was an error adding items to cart. Please try again.');
    });

  }



}

customElements.define('bulk-order-form', BulkOrderForm);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

// if (!document.querySelector('.main-product-gallery')) { alternate code from the live app
//   customElements.define('slider-component', SliderComponent);
// }

customElements.define('slider-component', SliderComponent);

class MenuCarousel extends SliderComponent {
  constructor() {
    super();
    this.enableSliderLooping = true;
  }
}

customElements.define('menu-carousel', MenuCarousel);


// custom code grg

class SliderComponentWithDots extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    //tweaking the next and prev buttons to accomodate multiple buttons
    //this.prevButton = this.querySelector('button[name="previous"]');
    //this.nextButton = this.querySelector('button[name="next"]');
    this.prevButton = this.querySelectorAll('button[name="previous"]');
    this.nextButton = this.querySelectorAll('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.desktopColumns = this.querySelector('input[id="desktop_columns"]');
    this.mobileColumns = this.querySelector('input[id="mobile_columns"]');
    this.showDesktopSlider = this.showMobileSlider = false;

    if (window.innerWidth >= 767) {
      this.showDesktopSlider = true;
      if (this.desktopColumns) {
        this.slidesPerPage = this.desktopColumns.value;
      }
      var mobileSlideshow = this.querySelector('.mobile_slideshow');
      if (mobileSlideshow) {
        mobileSlideshow.remove();
      }
      this.slider.classList.remove('slider--mobile');
    } else {
      this.showMobileSlider = true;
      this.slidesPerPage = this.mobileColumns.value;
      var desktopSlideshow = this.querySelector('.desktop_slideshow');
      if (desktopSlideshow) {
        desktopSlideshow.remove();
      }
      this.slider.classList.remove('slider--desktop');
    }

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    //tweaking the prev and next button to accomodate multiple buttons
    //this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.prevButton.forEach( button => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    })
    //this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.forEach( button => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    })

    this.sliderControlWrapper = this.querySelector('.slider-buttons');

    if (!this.sliderControlWrapper) return;
    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');


    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
  }

  initPages() {

    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.slidesPerPage = parseInt(this.slidesPerPage);
    if (this.sliderItemsToShow[this.slidesPerPage]) {
      this.sliderItemOffset = this.sliderItemsToShow[this.slidesPerPage].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    }

    if (this.showMobileSlider) {
      this.totalPages = this.sliderItemsToShow.length;
    }
    if (this.showDesktopSlider) {
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage;
    }
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentPage - 1]
        }
      }));
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0])) {
      //this.prevButton.setAttribute('disabled', 'disabled');
      this.prevButton.forEach(button => {
        button.setAttribute('disabled', 'disabled');
      })
    } else {
      //this.prevButton.removeAttribute('disabled');
      this.prevButton.forEach(button => {
        button.removeAttribute('disabled');
      })
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      //this.nextButton.setAttribute('disabled', 'disabled');
      this.nextButton.forEach(button => {
        button.setAttribute('disabled', 'disabled');
      })
    } else {
      //this.nextButton.removeAttribute('disabled');
      this.nextButton.forEach(button => {
        button.removeAttribute('disabled');
      })
    }

    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    //this.prevButton.removeAttribute('disabled');
    this.prevButton.forEach(button => {
      button.removeAttribute('disabled');
    })
    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    if (this.currentPage >= 1) {
      this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
      this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.slider.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}

/* end here slider-component-with- */

customElements.define('slider-component-with-dots', SliderComponentWithDots);

// custom code grg

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    this.updateOptions();
    this.updateMasterId();
    var mediaList = document.querySelector('.main-product__product__media-list');
    if(mediaList) {
      mediaList.classList.remove('loading-skeleton');
    }
  }

  onVariantChange() {
    console.log('running onVariantChange from VariantSelects')
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) { // this.currentVariant is taken from updateMasterId
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      console.log('onVariantChange updateMedia')
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
    console.log('this.currentVariant', this.currentVariant)
    if (this.currentVariant) {
      var variantOptionSelected = document.querySelector('.variant-options__selected');
      if (variantOptionSelected) {
        variantOptionSelected.innerHTML = this.currentVariant.title;
      }
      var colorName = this.currentVariant.title.toLowerCase();
      //to show color border above main image in product detail page
      colorName = colorName.split(' ').join('-');
      var productColorHeading = document.querySelector('.product-color-heading');
      if (productColorHeading) {
        productColorHeading.style.backgroundColor = 'var(--color-product-' + colorName + ')';
      }
    }
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;
    console.log(`[id^="MediaGallery-${this.dataset.section}"]`)
    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    console.log('mediaGalleries', mediaGalleries)
    mediaGalleries.forEach((mediaGallery) => {
      // this is the part where it will make the class MediaGallery invoke it's method setActiveMedia(mediaId, prepend)
      // but only responsible for updating the featured image of a product
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, false);
    });
    var mediaList = document.querySelector('.main-product__product__media-list');
    if(mediaList) {
      //mediaList.classList.add('loading-skeleton1');
    }
    //change the modal content to the current featured variant
    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
    loadProductCarousel();
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    if (productForms) {
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[data-productid]');
        if (input) {
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

      });
    }
    var pageLoad = this.getAttribute('data-page-load');
    if (pageLoad && pageLoad == 'true') {
      location.reload();
    }
    // location.reload();
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    var getFromProductUrl = this.getAttribute('data-product-url');
    if (getFromProductUrl && getFromProductUrl == 'true') {
      var fetchUrl = `${this.dataset.url}?variant=${this.currentVariant.id}`;
    } else {
      var fetchUrl = `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`;
    }
    fetch(fetchUrl)
      .then((response) => response.text())
      .then((responseText) => {
        var mediaList = document.querySelector('.main-product__product__media-list');
        if(mediaList) {
          mediaList.classList.remove('loading-skeleton');
        }
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;
        const btnDestination = document.getElementById(`btn-price-${this.dataset.section}`);
        const btnSource = html.getElementById(`btn-price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        if (btnSource && btnDestination) btnDestination.innerHTML = btnSource.innerHTML;

        //for slider products like gift-cards
        const sliderSource = html.querySelector('[id^="btn-price-"]');
        const sliderDestination = document.querySelector(`.slider-price-${this.dataset.section}`);
        if (sliderSource && sliderDestination) sliderDestination.innerHTML = sliderSource.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);
        const btnPrice = document.getElementById(`btn-price-${this.dataset.section}`);
        if (price) price.classList.remove('visibility-hidden');
        if (btnPrice) btnPrice.classList.remove('visibility-hidden');


        var link = document.querySelector(`#title-${this.dataset.section}` + ' a');
        var cardImageLink = document.querySelector(`#imageLink-${this.dataset.section}`);
        if (this.currentVariant.id) {
          if (link) {
            link.href = `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`;
          }
          if (cardImageLink) {
            cardImageLink.href = `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`;
          }
        }
        var mediaList = document.querySelector('.main-product__product__media-list');
        if(mediaList) {
          mediaList.classList.remove('loading-skeleton');
        }
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }
  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const btnPrice = document.getElementById(`btn-price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
    if (btnPrice) btnPrice.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
    this.variantType = this.getAttribute('data-variant-type');
  }

  updateOptions() {
    console.log('was I called updateOptions from VariantRadios')
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    var mediaList = document.querySelector('.main-product__product__media-list');
    if(mediaList) {
      //mediaList.classList.add('loading-skeleton1');
    }
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
    var variantSelectsDropdown = document.querySelector('variant-selects-dropdown[data-section="' + `${this.dataset.section}` + '"]');
    if (variantSelectsDropdown && (this.options.length > 0)) {
      var select = variantSelectsDropdown.querySelector('select[data-variant-type="' + this.variantType + '"]');
      if (select) {
        select.value = this.options[0];
        //select.dispatchEvent(new Event('change'));
        select.dispatchEvent(new Event('change',{ bubbles: true }));//to trigger onchange funcitonality
      }
    }
  }
}

customElements.define('variant-radios', VariantRadios);

class ColorSwatchRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    this.optionsSet = Array.from(this.querySelector('fieldset').querySelectorAll('input')).find((radio) => radio.checked);
    this.options = (typeof this.optionsSet == 'undefined') ? '' : this.optionsSet.value;
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      if (variant.id == this.options) {
        return variant.id;
      }
    });
  }

  updatePickupAvailability() { }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
  }

  updateShareUrl() { }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(`slider-price-${this.dataset.section}`);
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(`slider-price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }

  updateVariantInput() {
    document.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    const currentVariantInput = this.querySelector('fieldset input[value="' + this.currentVariant.id + '"]');
    if (currentVariantInput) {
      var priceItem = document.querySelector(`.slider-price-${this.dataset.section} .price-item`);
      if (priceItem) {
        priceItem.innerHTML = currentVariantInput.getAttribute('data-prices');
      }

      var link = document.querySelector(`#title-${this.dataset.section}` + ' a');
      var cardImageLink = document.querySelector(`#imageLink-${this.dataset.section}`);
      if (link) {
        link.href = currentVariantInput.getAttribute('data-variantUrl');
      }
      if (cardImageLink) {
        cardImageLink.href = currentVariantInput.getAttribute('data-variantUrl');
      }

      var img = document.querySelector(`.slider-media-${this.dataset.section} img`);
      if (img) {
        img.srcset = currentVariantInput.getAttribute('data-img');
      }
    }
  }
}

customElements.define('color-swatch-radios', ColorSwatchRadios);

//custom code grg

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

// custom code

class IframeVideoPlayPause extends HTMLElement {
  constructor() {
    super();
    // this.querySelector('.video-overlay').addEventListener('click', this.pauseVideo.bind(this));
    // this.iframe = this.querySelector('.iframe-container iframe');
    // this.type = this.getAttribute('data-type');
    // this.playButton = this.querySelector('.video-section__control-button');
    // if(!this.iframe.hasAttribute('src')) this.iframe.setAttribute('src', this.iframe.dataset.src);
    // if (this.type == "vimeo") {
    //   this.player = new Vimeo.Player(this.iframe);
    //   if (this.player && this.playButton) {
    //     this.playButton.addEventListener('click', this.clickVimeoButton.bind(this));
    //   }
    // } else {

    //   this.playButton.addEventListener('click', this.clickYoutubeButton.bind(this));
    // }

    this.type = this.getAttribute('data-type');
    this.playButton = null;
    this.overlay = null;
    this.iframe = null;
    this.player = null;

  }

  connectedCallback() {
    this.playButton = this.querySelector('.video-section__control-button');
    this.overlay = this.querySelector('.video-overlay');

    this.initLazyload();
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  initLazyload() {
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          //console.log('iframe intersecting')
          this.injectIframeFromTemplate();
          obs.unobserve(this);
        }
      });
    });
    observer.observe(this);
  }

  injectIframeFromTemplate() {
    const template = this.querySelector('template');
    //console.log('template', template)
    if (!template) return;

    const iframe = template.content.firstElementChild.cloneNode(true);
    iframe.setAttribute('src', iframe.dataset.src); // activate lazyload
    this.querySelector('.iframe-container').appendChild(iframe);
    this.iframe = iframe;

    if (this.type === "vimeo") {
      this.player = new Vimeo.Player(this.iframe);
      if(this.player) {
        this.playButton?.addEventListener('click', this.clickVimeoButton.bind(this));
      }
    } else {
      if(this.player) {
        this.playButton?.addEventListener('click', this.clickYoutubeButton.bind(this));
      }
    }

    this.overlay?.addEventListener('click', this.pauseVideo.bind(this));
  }

  pauseVideo(e) {
    if (this.playButton.classList.contains('pause')) {
      if (this.type == "vimeo") {
        if (!this.player.paused) {
          this.player.pause();
          this.changeButtonStatus('play');
        }
      } else {
        this.iframe.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
        this.changeButtonStatus('play');
      }
    }
  }
  changeButtonStatus(status) {
    if (status == 'play') {
      this.playButton.classList.remove('pause');
      this.playButton.classList.add('play');
      this.playButton.setAttribute('title', 'Play video');
      if(this.playButton.hasAttribute('aria-label')) {
        this.playButton.setAttribute('aria-label', 'Play video');
      }
    } else {
      this.playButton.classList.remove('play');
      this.playButton.classList.add('pause');
      this.playButton.setAttribute('title', 'Pause video');
      if(this.playButton.hasAttribute('aria-label')) {
        this.playButton.setAttribute('aria-label', 'Pause video');
      }
    }
  }
  clickYoutubeButton(e) {
    e.preventDefault();
    if (e.target.classList.contains('play')) {
      this.iframe.contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*');
      this.changeButtonStatus('pause');
    } else {
      this.iframe.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
      this.changeButtonStatus('play');
    }
  }
  clickVimeoButton(e) {
    var parent = this;
    if (e.target.classList.contains('play')) {
      this.player.getPaused().then(function (paused) {
        // `paused` indicates whether the player is paused
        parent.player.play().then(function () {
          parent.changeButtonStatus('pause');
        }).catch(function (error) {
          if (!parent.player.paused) {
            parent.changeButtonStatus('pause');
          }
        });
      });
    } else {
      this.player.pause();
      this.changeButtonStatus('play');
    }
  }
}
customElements.define('iframe-video-play-pause', IframeVideoPlayPause);

class VideoPlay extends HTMLElement {
  constructor() {
    super();
    const videoPlayer = this.querySelector('.video--playing');
    this.querySelector('.video-overlay').addEventListener('click', this.pauseVideo.bind(this));
    this.video = this.querySelector('.video');
    this.playButton = videoPlayer.querySelector('.video-section__control-button');
    this.playButton.addEventListener('click', this.playPauseVideo.bind(this));
  }
  playPauseVideo(e) {
    if (this.video.paused) {
      this.video.play();
      this.playButton.classList.remove('play');
      this.playButton.classList.add('pause');
      this.playButton.setAttribute('title', 'Pause video');
      if(this.playButton.hasAttribute('aria-label')) {
        this.playButton.setAttribute('aria-label', 'Pause video');
      }
    } else {
      this.video.pause();
      this.playButton.classList.remove('pause');
      this.playButton.classList.add('play');
      this.playButton.removeAttribute('title', 'Pause video');
    }
  }
  pauseVideo(e) {
    if (!this.video.paused) {
      this.video.pause();
      this.playButton.classList.remove('pause');
      this.playButton.classList.add('play');
      this.playButton.setAttribute('title', 'Play video');
      if(this.playButton.hasAttribute('aria-label')) {
        this.playButton.setAttribute('aria-label', 'Play video');
      }
    }
  }
}
customElements.define('video-play', VideoPlay);



// custom code



// optional code

const checkCompDescHeight = () => {
  let compare_slider_card__text_height = 0;
  let compare_slider_card__text = document.querySelectorAll('.compare-slider-card__info__text');
  if (compare_slider_card__text) {
    compare_slider_card__text.forEach((e) => {
      e.style.height = "unset";
      compare_slider_card__text_height = e.clientHeight >= compare_slider_card__text_height ? e.clientHeight : compare_slider_card__text_height;
    });
    compare_slider_card__text.forEach((elem) => {
      elem.style.height = `${compare_slider_card__text_height}px`;
    });
  }
}
checkCompDescHeight();
window.addEventListener("resize", () => {
  checkCompDescHeight();
});

// optional code


// custom code grg

/**************carousel for product page gallery start*************************/
const elementInViewport = (el) => {
  var top = el.offsetTop;
  var left = el.offsetLeft;
  var width = el.offsetWidth;
  var height = el.offsetHeight;

  while (el.offsetParent) {
    el = el.offsetParent;
    top += el.offsetTop;
    left += el.offsetLeft;
  }

  return (
    top >= window.pageYOffset &&
    left >= window.pageXOffset &&
    (top + height) <= (window.pageYOffset + window.innerHeight) &&
    (left + width) <= (window.pageXOffset + window.innerWidth)
  );
}
function carouselSlideClick(event) {
  const liCount = event.currentTarget.getAttribute('data-count');
  const mainProductGallery = document.querySelector('.main-product-gallery');
  if (mainProductGallery) {
    let shouldSkip = false;
    mainProductGallery.querySelectorAll('li').forEach((li, index) => {
      if (shouldSkip) {
        return;
      }
      if (index + 1 == parseInt(liCount)) {
        // li.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        li.scrollIntoView({ block: "center" });
        shouldSkip = true;
        return false;
      }
    });
  }
}
const checkGalleryModal = () => {
  const productModal = document.querySelector('product-modal.spinstudio-holder')
  if(productModal) {
    if(productModal.querySelector('img')) {
      productModal.querySelectorAll('img').forEach(img => {
        img.classList.remove('spinstudio-hidden');
      });
    }
    clearInterval(spin360Interval);
  }
}

const loadSpinBtn = () => {
  const isModal360Opener = document.querySelector('.modal-360-opener');
  if(isModal360Opener) {
    if(isModal360Opener.getAttribute('data-tags') == 'spin-modal') {
      spin360Interval = setInterval(checkGalleryModal, 1000);
    }
    const spinModalOpener = document.querySelectorAll('.modal-360-opener');
    spinModalOpener.forEach(btn => {
      if(btn.getAttribute('data-tags') == 'spin-modal') {
        document.querySelector('.show-product-360-title').classList.remove('visually-hidden');
        btn.addEventListener('click', function() {
          const spinModal = document.querySelector(this.getAttribute('data-modal'));
          if (spinModal) spinModal.show(btn);
        });
      }
    });
  }
}
function intersectionHandler(entry) {
  const mainProductGallery = document.querySelector('.main-product-gallery');
  const index = [...mainProductGallery.childNodes].findIndex(item => item === entry.target)
  document.querySelector('.images-indicator-item.active')?.classList.remove('active');
  const divs = document.querySelectorAll('.images-indicator-item');
  if (divs[index]) {
    divs[index].classList.add('active');
  }
}
let minId = null;
let maxId = null;
let debounceTimeout = null;

function applyChanges() {
  if(minId != null && maxId != null) {
    document.querySelector('.images-indicator-item.active')?.classList.remove('active');
    const divs = document.querySelectorAll('.images-indicator-item');
    if(minId == 0) {
      if (divs[minId]) {
        divs[minId].classList.add('active');
      }
    } else {
      if (divs[maxId]) {
        divs[maxId].classList.add('active');
      }
    }
  }
  // perform action on elements with Id between min and max
  minId = null;
  maxId = null;
}
const loadVariantCarousel = () => {
  const mainProductGallery = document.querySelector('.main-product-gallery');
  if (mainProductGallery) {
    const allLi = mainProductGallery.querySelectorAll('li');
    var indicatorBox = document.querySelector('.images-indicator-box');
    if (indicatorBox) {
      indicatorBox.classList.remove('visually-hidden');
      var newLi = '';
      for (var i = 1; i <= allLi.length; i++) {
        if(allLi.length > 1) {
          var addClass = (i == 1) ? 'active' : '';
        } else {
          var addClass = 'visually-hidden';
        }
        newLi += '<li class="images-indicator-item ' + addClass + '" data-count=' + i + ' </li>';
      }
      indicatorBox.querySelector('.images-indicator-list').innerHTML = newLi;
      indicatorBox.querySelectorAll('.images-indicator-list li').forEach(li => {
        li.addEventListener('click', carouselSlideClick.bind(this));
      });
      /******************************* */
      const config = {
        // rootMargin: '-50px 0px -55%',
        threshold: [1]
      };
      let observer = new IntersectionObserver(function (entries, self) {
        clearTimeout(debounceTimeout);
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const entryId = parseInt(entry.target.getAttribute('data-pos'));
            if (minId === null || maxId === null) {
              minId = entryId;
              maxId = entryId;
            } else {
              minId = Math.min(minId, entryId);
              maxId = Math.max(maxId, entryId);
            }
            intersectionHandler(entry);
          }
        });
        debounceTimeout = setTimeout(applyChanges, 500);
      }, config);
      let idCounter = 0;

      allLi.forEach(li => {
        li.setAttribute('data-pos', idCounter++);
        observer.observe(li);
      });
      /******************************************/
      indicatorBox.querySelector('.indicator-btn--up')?.addEventListener('click', carouselUpBtnClick.bind(this));
      indicatorBox.querySelector('.indicator-btn--down')?.addEventListener('click', carouselDownBtnClick.bind(this));
      mainProductGallery.parentElement.querySelectorAll('.slider-button').forEach((elm) => {
        if(elm.classList.contains('spiceFeatPrev') || elm.classList.contains('spiceFeatNext')) {
          // elm.remove();
        }
      });
      if(customElements.get('slider-component') === undefined)
        customElements.define('slider-component', SliderComponent);
      else  {
        const sliderNextBtn = mainProductGallery.parentElement.querySelector('.slider-button.slider-button--next');
        //in case of changing product variant, default slider is making next arrow disabled
        if(sliderNextBtn) {
          setTimeout(function() {
            if(sliderNextBtn.hasAttribute('disabled')) {
              sliderNextBtn.removeAttribute('disabled')
            }
          },2500);
        }
      }
    }
  }
}
const loadCarousel = () => {
  //loadcarousel in media gallery in product detail page when classlist will have spiceFeat class
  if (document.querySelector('.main-product-gallery').classList.contains('spiceFeat')) {
    loadSpinBtn();
    loadVariantCarousel();
    const rcContainer = document.querySelector('#rc_container');
    if(!rcContainer || (rcContainer && rcContainer.innerHTML == '')) {
      if(document.querySelector('afterpay-placement')) {
        document.querySelector('afterpay-placement').style.display = 'block';
      }
    }
    clearInterval(carouselInterval);
  }
}

function loadProductCarousel() {
  // this set time out is responsible for the scroll to top behavior when changing the variant
  setTimeout(function () {
    window.scrollTo(0, 0);
  }, 10);
  if (document.querySelector('.main-product-gallery')) {
    if(document.querySelector('variant-radios') || document.querySelector('variant-selects')) { // variant selectors in the form of radios our dropdown select
      // store it to carouselInterval so we can clear interval later 
      console.log('setInterval(loadCarousel')
      carouselInterval = setInterval(loadCarousel, 1000);
    } else {
      loadSpinBtn();
      loadVariantCarousel();
    }
  }
}

function carouselUpBtnClick(event) {
  var activeLiCount = event.currentTarget.parentElement.querySelector('.images-indicator-item.active').getAttribute('data-count');
  activeLiCount = parseInt(activeLiCount);
  if (activeLiCount > 1) {
    event.currentTarget.parentElement.querySelector(`.images-indicator-item[data-count="${activeLiCount - 1}"]`)?.click();
  }
}
function carouselDownBtnClick(event) {
  const mainProductGallery = document.querySelector('.main-product-gallery');
  if (mainProductGallery) {
    const allLi = mainProductGallery.querySelectorAll('li');
    var activeLiCount = event.currentTarget.parentElement.querySelector('.images-indicator-item.active').getAttribute('data-count');
    activeLiCount = parseInt(activeLiCount);
    if (activeLiCount < allLi.length) {
      event.currentTarget.parentElement.querySelector(`.images-indicator-item[data-count="${activeLiCount + parseInt(1)}"]`)?.click();
    }
  }

}
var lastScrollTop = 0;
/*window.addEventListener('scroll', () => {
  const mainProductGallery = document.querySelector('.main-product-gallery');
  if (mainProductGallery) {
    const allLi = mainProductGallery.querySelectorAll('li');
    for (let i = 0; i < allLi.length; ++i) {
      const isElementInViewport = elementInViewport(allLi[i]);
      if (isElementInViewport) {
        document.querySelector('.images-indicator-item.active')?.classList.remove('active');
        const divs = document.querySelectorAll('.images-indicator-item');
        if (divs[i]) {
          divs[i].classList.add('active');
        }
        break;
      }
    }
  }
});*/
/**************carousel for product page gallery end*************************/

// custom code grg

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}



///////// ADDITIONAL CUSTOM CLASSES

if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
    }

    hide() {
      super.hide();
    }

    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }

    showActiveMedia() {
      this.querySelectorAll(`[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`).forEach((element) => {
          element.classList.remove('active');
        }
      )
      const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`);
      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
      activeMedia.classList.add('active');
      activeMedia.scrollIntoView();

      const container = this.querySelector('[role="document"]');
      container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

      if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
        activeMedia.loadContent();
    }
  });
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      console.log('setting active media from global.js media-gallery classss')
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        // prepend is opposite to appendChild in the sense that The .prepend() method inserts a set of Node objects or string objects before the first child of the parent element.
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        // resetPages() comes from slider components
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      console.log('do we have this.elements.thumbnails', this.elements.thumbnails)
      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}

class AnnouncementDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.triggers = document.querySelectorAll('.js-open-announcement-drawer');
  }
  
  connectedCallback() {
    // Register the click event on the trigger.
    this.triggers.forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(this.trigger);
      });
    });
  
    // This adds extra space to the bottom of the body so that mobile users can see the footer links.
    const spacerElement = document.createElement('div');
    spacerElement.classList.add('mobile-only');
    spacerElement.style.height = '72px';
    spacerElement.innerHTML = '&nbsp;';
    document.body.appendChild(spacerElement);



  }
  
  open(triggeredBy) {
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});
  
    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = document.getElementById('AnnouncementDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
  }
  
  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
  }
}

// if (!customElements.get('announcement-drawer')) { optional way of adding this
//   customElements.define('announcement-drawer', AnnouncementDrawer);
// }

customElements.define('announcement-drawer', AnnouncementDrawer);

if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      this.modalContent.querySelector('variant-radios,variant-selects').setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
        variantSelect.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/**************Script for our PDP Animate in to View START GRG *************************/

const observeElements = document.querySelectorAll('.grg-observe-me-true')

const options = {
  root: null,
  threshold: 0,
  rootMargin: "300px -300px -300px -300px"
}


if (observeElements) {
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if(entry.isIntersecting) {
        entry.target.classList.add('is-intersecting')
      }

      if(!entry.isIntersecting) {
        entry.target.classList.remove('is-intersecting')
        //entry.target.classList.remove('')
      }

    })

  }, options)


  observeElements.forEach(element => {
    observer.observe(element);
  })
  

}

/**************Script for our PDP Animate in to View END GRG *************************/

/************** GRG Object Oriented Scripts Start *************************/

// GRG script for CTA & Sliding Cards
// link to this code https://www.youtube.com/watch?v=6QE8dXq9SOE
class GrgSliderCard extends HTMLElement {
  constructor() {
    super()

    //the below is how to Declaring Multiple Variables in a Single Line sing a comma-separated list with let
    //this.isDragging = false, startX, startScrollLeft, timeoutId;

    this.isDragging = false;
    this.startX;
    this.startScrollLeft;
    this.timeoutId;

    this.mainSection = document.querySelector(".collection.slider-steps-section")
    this.wrapper = this.querySelector(".grg-slider-card__wrapper")
    this.carousel =  this.querySelector(".grg-slider-card__carousel")
    this.arrowBtns = this.querySelectorAll(".grg-slider-card__wrapper i")
    this.firstCardWidth = this.carousel.querySelector(".grg-slider-card__card").offsetWidth;

    //spread syntax or rest parameter >> allows an iterable (like an array or string) to be expanded into individual elements.
    this.carouselChildrens = [...this.carousel.children]

    console.log('this.carouselChildrens', this.carouselChildrens)
    // Get the number of cards that can fit in the carousel at once
    this.cardPerView = Math.round(this.carousel.offsetWidth / this.firstCardWidth) 
    this.originLeft = this.carousel.scrollLeft
    console.log('this.cardPerView', this.cardPerView)

    //console.log('originLeft', this.carousel.scrollLeft)
    //console.log(this.carousel, this.carousel.offsetWidth)
    //console.log(this.carousel, this.carousel.scrollWidth - this.carousel.offsetWidth)
  }

  connectedCallback() {
    // connected callback event hook is where I set all the major event listeners

    
    if(this.carouselChildrens.length > 5) {

      this.carousel.addEventListener("mousedown", this.dragStart.bind(this))
      this.carousel.addEventListener("mousemove", this.dragging.bind(this))
  
  
  
      //this will stop the cards from sliding if the mousedownbutton is release
      document.addEventListener("mouseup", this.dragStop.bind(this))
      this.wrapper.addEventListener("mouseenter", clearTimeout(this.timeoutId))
      this.wrapper.addEventListener("mouseleave", this.autoPlay)

    }


    // add event listeners for the arrow buttons to scroll the carousel left and right
    if(this.arrowBtns) {
      this.arrowBtns.forEach( btn => {
          btn.addEventListener("click", () => {
            //console.log(btn.id)
            this.carousel.scrollLeft += btn.id === "left" ? -this.firstCardWidth: this.firstCardWidth
          })
      })
    }


    //if we want to do the infinite scroll and cards are greater than 5
    if(this.mainSection.dataset.scrollInfinite  == "true" && this.carouselChildrens.length > 5 ) {

        // insert copies of the last few cards to beginning of carousel for infinite scrolling
        // notice the negative value of this.cardPerView?
        // this starts counting from the end of the array extracting the last (this.cardPerView) elements
          this.carouselChildrens.slice(-this.cardPerView).reverse().forEach(card => {
            //console.log('card.outerHTML', card)
            let listElement = document.createElement("li");
            listElement.classList.add('grg-slider-card__card')
            listElement.classList.add('head')
            listElement.innerHTML = card.innerHTML
            this.carousel.insertAdjacentElement("afterbegin", listElement)
          });

          // insert copies of the first few cards to the end of carousel for infinite scrolling
          this.carouselChildrens.slice(0, this.cardPerView).forEach(card => {
            let listElement = document.createElement("li");
            listElement.classList.add('grg-slider-card__card')
            listElement.classList.add('tail')
            listElement.innerHTML = card.innerHTML
            this.carousel.insertAdjacentElement("beforeend", listElement)
          });

          this.carousel.addEventListener("scroll", this.infiniteScroll.bind(this))

    }


    //this.autoplayGrg()


  }

  autoplayGrg() { // will need to invoke this somewhere if this feature is needed
    if(window.innerWidth < 800) return; // Return if window is smaller than 800
    // autoplay the carousel after every 2500ms
    this.timeoutId = setTimeout(() => this.carousel.scrollLeft += this.firstCardWidth, 2500);
  }

  infiniteScroll() {
    // if the carousel is at the beginning, scroll to the end
    //console.log('infinite scroll', this.carousel)
    //console.log('currentLeft', this.carousel.scrollLeft)
    if(this.carousel.scrollLeft === this.originLeft) { // may I add and IF it is infinite scroll
      //console.log("You've reached the left end")
      this.carousel.classList.add("no-transition") // remove the smoot scroll animation in order to look like proper infinite scrolling effect
      this.carousel.scrollLeft = this.carousel.scrollWidth - ( 2 * this.carousel.offsetWidth);
      this.carousel.classList.remove("no-transition")
      
    } 
    // if the carousel is at the end, scroll to the beginning
    else if(Math.ceil(this.carousel.scrollLeft) === this.carousel.scrollWidth - this.carousel.offsetWidth) { // Math.ceil method rounds a number rounded UP to the nearest integer

      //console.log("You've reached the right end")
      this.carousel.classList.add("no-transition") // remove the smoot scroll animation in order to look like proper infinite scrolling effect
      this.carousel.scrollLeft = this.carousel.offsetWidth;
      this.carousel.classList.remove("no-transition")
    }

    //clear existing timeout & start autoplay if mouse is not hovering over carousel
    clearTimeout(this.timeoutId);
    //if(!this.wrapper.matches(":hover")) this.autoplayGrg()
  }

  dragStart(e) {
    this.isDragging = true;
    this.carousel.classList.add("dragging");

    //records the initial cursor and scroll position of the carousel
    this.startX = e.pageX;
    this.startScrollLeft = this.carousel.scrollLeft;
  }

  dragging(e) {
    //console.log(e.pageX);
    if(!this.isDragging) return; // if isDragging is false return from here
    //updates the scroll position of the carousel based on the cursor movement
    this.carousel.scrollLeft = this.startScrollLeft - (e.pageX - this.startX);
  }

  dragStop() {
    this.isDragging = false;
    //console.log('carouse', this.carousel)
    this.carousel.classList.remove("dragging")

  }



  


}

customElements.define('grg-slider-card', GrgSliderCard);

/************** GRG Object Oriented Scripts End *************************/


class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.grg-meg-menu-custom');
    this.headerBounds = {};
    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.disclosures = this.header.querySelectorAll('header-menu');
    

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => this.preventReveal = true;

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
    this.registerEvents();
  }

  connectedCallback() {

  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  registerEvents() {
    // Prevent mouse hover events on design mode.
    if (window.Shopify?.designMode) return;

    // Adds mouse hover functionality to menu items
    
    this.disclosures.forEach(disclosure => {
      const modalOverlayElement = disclosure.querySelector('.modal-overlay');

      disclosure.addEventListener('mouseover', (event) => {
        disclosure.open();
        
        if (event.target === modalOverlayElement) {
          disclosure.close();
        }
      });
      
      disclosure.parentNode.addEventListener('mouseleave', () => {
        disclosure.close();
      });
    });
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Scroll position is past the site header
    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.top) {
      if (this.preventHide) {
        requestAnimationFrame(this.hide.bind(this));
      }

      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }

    } else if (scrollTop <= this.headerBounds.top) {
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
  }

  reveal() {
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach(disclosure => disclosure.close());
  }
}

customElements.define('sticky-header', StickyHeader);





class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('.disclosure__list-wrapper'),
    };
    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  hidePanel() {
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    this.elements.button.focus();
    this.elements.panel.toggleAttribute('hidden');
    this.elements.button.setAttribute(
      'aria-expanded',
      (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
    );
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }
}

customElements.define('localization-form', LocalizationForm);


